import { Command } from "lucide-react";
import { hasAnyRole, OperationsPrincipals } from "../../auth/roles";
import { useShownRoles } from "../../auth/useShownRoles";
import { useRelease150 } from "../../lib/release150";
import { SearchButton, SearchPanel } from "./GlobalSearch";
import { openHeaderPanel } from "./headerStore";
import { RefreshButton } from "./RefreshButton";
import { NotificationBell } from "../notifications/NotificationBell";
import { SyncStatusChip } from "./SyncStatusChip";

// Release 1.5.0 header tools, placed by AppShell around the ticker: the inline search box on the widest screens, then
// the search button (narrower screens), Go to, the sync chip and Refresh. A phone shows icons; the chip's menu has the words.

/** Fetches the Go to palette's code ahead of the first use (pointer over or focus on the button). */
export const prefetchGoTo = () => void import("./GoToPalette");

/** The header tools show only with release 1.5.0 on, and only for a person with an Operations role. */
export function useHeaderToolsShown(): boolean {
  const release150 = useRelease150();
  const roles = useShownRoles();
  return release150 && hasAnyRole(roles, OperationsPrincipals);
}

/** The inline search box (2xl and wider). */
export function HeaderSearchSlot() {
  if (!useHeaderToolsShown()) return null;
  return (
    <div className="hidden w-72 flex-none 2xl:block" data-testid="header-search-slot" data-tour="search">
      <SearchPanel variant="inline" />
    </div>
  );
}

/** The header's "Go to…" button (Ctrl+P). The label shows on the widest screens; a phone shows the icon. */
export function GoToButton() {
  return (
    <button
      type="button"
      className="inline-flex h-8 flex-none items-center gap-1.5 rounded-md border border-slate-200 px-1.5 text-xs md:px-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      onClick={() => openHeaderPanel("goto")}
      onMouseEnter={prefetchGoTo}
      onFocus={prefetchGoTo}
      aria-keyshortcuts="Control+P Control+Shift+P"
      title="Go to a page, an action or a recent record (Ctrl+P)"
      aria-label="Go to…"
      data-testid="goto-button"
    >
      <Command aria-hidden="true" className="h-3.5 w-3.5" />
      <span className="hidden 2xl:inline">Go to…</span>
      <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1 font-sans text-[10px] text-slate-500 2xl:inline">Ctrl P</kbd>
    </button>
  );
}

/** Search (below 2xl), Go to, the sync chip and Refresh. */
export function HeaderActions() {
  if (!useHeaderToolsShown()) return null;
  return (
    <div className="relative flex flex-none items-center gap-1 md:gap-2" data-testid="header-actions">
      <SearchButton />
      <GoToButton />
      <SyncStatusChip />
      <RefreshButton />
      <NotificationBell />
    </div>
  );
}
